import "./styles.scss";

import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";

import NeedHelp from "./components/NeedHelp";
import PageLoader from "components/PageLoader";
import UsageChart from "./components/UsageChart";
import { WebsiteStatus, WebsiteOverview } from "./components";
import DevelopmentMobile from "components/DevelopmentMobile";
import Notifications from "page_components/shared/dashboard/Notifications";
import DashboardContentLayout from "components/layouts/DashboardContentLayout";
import ScrollTopMobile from "components/ScrollTopMobile";
import PlaceholderPage from "components/PlaceholderPage";

import { useDashboard } from "context/client/dashboard";

import toHoursAndMinutes from "libs/helpers/toHoursAndMinutes";
import useResponsive from "libs/helpers/useResponsive";

import { getListItems } from "./data";

const mainClass = "dashboard-client-content";

const DashboardClientContent = () => {
  const { t } = useTranslation();
  const { max } = useResponsive();

  const { dashboard, uptime, websites, loading, isDashboardEmpty } =
    useDashboard();
  const list_items = getListItems(dashboard?.website_data, uptime, t);

  const toNumber = (value) => {
    const serializedValue =
      value?.$numberDecimal ??
      value?.$numberDouble ??
      value?.$numberInt ??
      value?.$numberLong ??
      value?.value ??
      value;

    if (serializedValue == null || serializedValue === "") return null;

    const numericValue = Number(serializedValue);

    return Number.isFinite(numericValue) ? numericValue : null;
  };
  const formatTime = (value, zeroAsUnavailable = false) => {
    const numericValue = toNumber(value);

    return numericValue === null || (zeroAsUnavailable && numericValue === 0)
      ? "-"
      : toHoursAndMinutes(numericValue)?.formatted;
  };
  const monthStart = moment().startOf("month").format("YYYY-MM-DD");
  const monthEnd = moment().endOf("month").format("YYYY-MM-DD");
  const usageDetailsHref = `/tasks?time_over=true&start_due_date=${monthStart}&end_due_date=${monthEnd}`;

  const breadcrumps = [
    {
      label: t("Dashboard"),
    },
  ];

  const actions = [
    {
      label: t("Add new website"),
      href: "/websites/new",
      event: () =>
        window.gtag("event", "new_website", {
          click_text: t("Add new website"),
          click_url: `/websites/new`,
        }),
    },
    ...(websites?.length > 0
      ? [
          {
            type: "create_client_task",
            onboarding_button: true,
          },
        ]
      : []),
  ];

  return (
    <DashboardContentLayout actions={actions} breadcrumps={breadcrumps}>
      <div className={mainClass}>
        {isDashboardEmpty ? (
          <PlaceholderPage
            title={t("Add your website to Webinity")}
            text={t(
              "Just a few simple steps away from peace of mind with managing your website or store. Let us take care of your website, and you enjoy the peace and time to grow your business.",
            )}
            button={{
              label: t("Add website"),
              href: "/websites/new",
            }}
          />
        ) : loading ? (
          <PageLoader />
        ) : (
          <>
            <WebsiteStatus
              data={dashboard?.website_data}
              list_items={list_items}
            />
            <div className={`${mainClass}__col`}>
              <div className={`${mainClass}__col__row`}>
                {max.sm ? (
                  <>
                    <WebsiteOverview
                      data={list_items.filter((item) => !!item.content)}
                    />
                    <WebsiteOverview
                      data={list_items.filter((item) => !!!item.content)}
                    />
                    <DevelopmentMobile
                      available={formatTime(
                        dashboard?.website_data?.time_used?.available_in_plan,
                        true,
                      )}
                      left={formatTime(
                        dashboard?.website_data?.time_used?.time_left,
                        true,
                      )}
                      exceeding={formatTime(
                        dashboard?.website_data?.time_used?.time_exceeding_plan,
                      )}
                      moreHref={usageDetailsHref}
                      variant="dashboard"
                    />
                  </>
                ) : (
                  <UsageChart data={dashboard?.website_data} />
                )}
              </div>
              <div
                className={`${mainClass}__col__row ${mainClass}__col__row--notifications`}
              >
                <Notifications />
                <NeedHelp />
              </div>
            </div>
            {max.sm && (
              <div className={`${mainClass}__scroll`}>
                <ScrollTopMobile />
              </div>
            )}
          </>
        )}
      </div>
    </DashboardContentLayout>
  );
};

export default DashboardClientContent;
